import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;
  const articleIndex = articles.findIndex((a) => a.slug === slug);
  const prevArticle = articleIndex > 0 ? articles[articleIndex - 1] : null;
  const nextArticle = articleIndex < articles.length - 1 ? articles[articleIndex + 1] : null;

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article?.categorySlug && a.slug !== slug)
    .slice(0, 4);

  useEffect(() => {
    if (!article) return;
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        image: article.coverImage,
        publisher: {
          '@type': 'Organization',
          name: '51吃瓜官网',
          url: 'http://yjpslu.cn/',
          logo: {
            '@type': 'ImageObject',
            url: 'http://yjpslu.cn/logo.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `http://yjpslu.cn/article/${slug}`,
        },
        articleSection: article.category,
        keywords: article.tags.join(', '),
        articleBody: article.content.replace(/<[^>]*>/g, ''),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://yjpslu.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.category,
            item: `http://yjpslu.cn/category/${article.categorySlug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: `http://yjpslu.cn/article/${slug}`,
          },
        ],
      },
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'article-schema';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    const existing = document.querySelectorAll('script[id="article-schema"]');
    if (existing.length > 1) {
      existing[0].remove();
    }

    return () => {
      const el = document.querySelector('script[id="article-schema"]');
      if (el) document.head.removeChild(el);
    };
  }, [article, slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 text-sm"
            >
              {t('backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14 bg-background-50">
        <article className="w-full max-w-[860px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav
            className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap"
            aria-label="Breadcrumb"
          >
            <Link
              to="/"
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {t('breadcrumbHome')}
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link
              to={`/category/${article.categorySlug}`}
              className="hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-700 line-clamp-1">
              {article.title}
            </span>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                  {t('hot')}
                </span>
              )}
              {article.isExclusive && (
                <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap">
                  {t('exclusive')}
                </span>
              )}
            </div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishDate} {article.publishTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {article.views.toLocaleString()} {t('views')}
              </span>
            </div>
          </header>

          {/* Cover image */}
          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover object-top"
              title={article.title}
            />
          </div>

          {/* Article content */}
          <div
            className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex items-center gap-2 mb-10 flex-wrap">
            <span className="text-sm text-foreground-500">{t('tags')}：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Prev / Next navigation */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t border-background-200 pt-6 mb-10">
            {prevArticle ? (
              <Link
                to={`/article/${prevArticle.slug}`}
                className="flex-1 flex items-start gap-2 group cursor-pointer"
              >
                <i className="ri-arrow-left-line text-foreground-400 mt-0.5 group-hover:text-primary-500 transition-colors" />
                <div className="min-w-0">
                  <span className="text-xs text-foreground-400 block mb-0.5">
                    {t('prevArticle')}
                  </span>
                  <span className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {prevArticle.title}
                  </span>
                </div>
              </Link>
            ) : (
              <div className="flex-1" />
            )}
            {nextArticle ? (
              <Link
                to={`/article/${nextArticle.slug}`}
                className="flex-1 flex items-start gap-2 group cursor-pointer justify-end text-right"
              >
                <div className="min-w-0">
                  <span className="text-xs text-foreground-400 block mb-0.5">
                    {t('nextArticle')}
                  </span>
                  <span className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {nextArticle.title}
                  </span>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 mt-0.5 group-hover:text-primary-500 transition-colors" />
              </Link>
            ) : (
              <div className="flex-1" />
            )}
          </div>

          {/* Related articles */}
          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200 pt-10">
              <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-5">
                {t('relatedArticles')}
              </h3>
              <div className="flex flex-col gap-3">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} variant="compact" />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}